export const serviceAdvocateIconTokens = [
    'arrowRight',
    'award',
    'badgeCheck',
    'checkCircle',
    'externalLink',
    'gauge',
    'globe',
    'laptop',
    'messageCircle',
    'rocket',
    'search',
    'shieldCheck',
    'smartphone',
    'sparkles',
    'star',
    'trendingUp',
    'users',
] as const;

export type ServiceAdvocateIconToken = (typeof serviceAdvocateIconTokens)[number];

export const serviceAdvocateTokens = {
    colors: {
        background: '#ffffff',
        surface: '#ffffff',
        surfaceSoft: '#f7fbf9',
        text: '#07142f',
        textSoft: '#243452',
        textMuted: '#667085',
        accent: '#16a34a',
        accentDark: '#0f8738',
        accentSoft: 'rgba(22, 163, 74, 0.13)',
        accentWash: 'rgba(22, 163, 74, 0.08)',
        accentContrast: '#ffffff',
        border: 'rgba(7, 20, 47, 0.1)',
        borderStrong: 'rgba(7, 20, 47, 0.16)',
        shadow: '0 18px 46px rgba(7, 20, 47, 0.08)',
        softShadow: '0 10px 28px rgba(7, 20, 47, 0.07)',
        gold: '#f8b400',
        googleBlue: '#4285f4',
        googleRed: '#ea4335',
        googleYellow: '#fbbc05',
        googleGreen: '#34a853',
    },
    radius: {
        shell: 8,
        tile: 8,
        media: 8,
        button: 999,
        icon: 999,
        avatar: 999,
    },
    spacing: {
        sectionY: 36,
        sectionX: 20,
        shellPadding: 26,
        shellPaddingMobile: 18,
        topGap: 20,
        tileGap: 14,
        proofGap: 0,
        actionGap: 18,
    },
    sizes: {
        maxWidth: 1440,
        contentMaxWidth: 780,
        mediaWidth: 360,
        mediaMinHeight: 184,
        icon: 62,
        actionMinHeight: 50,
        avatar: 50,
    },
    typography: {
        fontFamily:
            'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        eyebrow: {
            fontSize: 13,
            lineHeight: 1.15,
            fontWeight: 850,
            letterSpacing: 0,
            textTransform: 'uppercase' as const,
        },
        title: {
            fontSize: 40,
            tabletFontSize: 34,
            mobileFontSize: 28,
            lineHeight: 1.08,
            fontWeight: 850,
            letterSpacing: 0,
        },
        description: {
            fontSize: 18,
            tabletFontSize: 17,
            mobileFontSize: 16,
            lineHeight: 1.55,
            fontWeight: 500,
            letterSpacing: 0,
        },
        tileTitle: {
            fontSize: 18,
            mobileFontSize: 18,
            lineHeight: 1.2,
            fontWeight: 800,
            letterSpacing: 0,
        },
        tileText: {
            fontSize: 15,
            mobileFontSize: 15,
            lineHeight: 1.42,
            fontWeight: 500,
            letterSpacing: 0,
        },
        proofTitle: {
            fontSize: 16,
            mobileFontSize: 16,
            lineHeight: 1.3,
            fontWeight: 760,
            letterSpacing: 0,
        },
        proofText: {
            fontSize: 14,
            mobileFontSize: 14,
            lineHeight: 1.45,
            fontWeight: 500,
            letterSpacing: 0,
        },
        action: {
            fontSize: 16,
            mobileFontSize: 15,
            lineHeight: 1,
            fontWeight: 850,
            letterSpacing: 0,
        },
    },
    defaults: {
        eyebrow: 'Why grow leads',
        title: 'Built to help local businesses get found',
        description:
            'We build fast, conversion-focused websites with local SEO foundations so you can rank higher, attract more customers, and grow with confidence.',
        features: [
            {
                title: 'Fast launch',
                description: 'Get online quickly with our streamlined process.',
                icon: 'rocket' as ServiceAdvocateIconToken,
            },
            {
                title: 'SEO-ready structure',
                description: 'Clean, optimised foundations built for local search.',
                icon: 'search' as ServiceAdvocateIconToken,
            },
            {
                title: 'Trusted local support',
                description: 'Australian-based team here to help you grow.',
                icon: 'users' as ServiceAdvocateIconToken,
            },
        ],
        proof: {
            rating: '5.0',
            title: 'Trusted by local businesses',
            description: '20+ reviews',
            showStars: true,
        },
        testimonial: {
            quote: 'Grow Leads built our website and we started getting enquiries within days. Highly recommend!',
            authorName: 'Sarah M.',
            authorRole: 'Landscapes by Nature',
        },
        checklist: ['Local business focused', 'Mobile friendly', 'Fast turnaround'],
        primaryAction: {
            label: 'Book a Free Consultation',
            href: '#contact',
            icon: 'arrowRight' as ServiceAdvocateIconToken,
        },
        secondaryAction: {
            label: 'Explore related services',
            href: '#services',
            icon: 'arrowRight' as ServiceAdvocateIconToken,
        },
    },
} as const;

export type ServiceAdvocateColorToken = keyof typeof serviceAdvocateTokens.colors;
